// context/AuthContext.tsx

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types/User";

type AuthContextType = {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (userData: User) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  login: () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const stored = localStorage.getItem("e-user");
    if (!stored) {
      setLoading(false);
      return;
    }

    try {
      const parsed = JSON.parse(stored);
      if (
        parsed &&
        typeof parsed === "object" &&
        typeof parsed.user_id === "number" &&
        typeof parsed.token === "string" &&
        typeof parsed.user_email === "string"
      ) {
        setUser(parsed);
      }
    } catch {
      // ignore parse error
    } finally {
      setLoading(false); // ✅ always set false after attempt
    }
  }, []);

  const login = (userData: User) => {
    localStorage.setItem("e-user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("e-user");
    setUser(null);
  };

  const isAuthenticated = !!user;

  useEffect(() => {
    if (
      user &&
      (typeof user !== "object" ||
        typeof user.user_id !== "number" ||
        typeof user.token !== "string" ||
        typeof user.user_email !== "string")
    ) {
      logout();
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
